import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { motion, AnimatePresence } from "framer-motion";

import SophieAvatar from "../Bots/Sophie.jpg";
import EleanorAvatar from "../Bots/Eleanor.jpg";
import ThomasAvatar from "../Bots/Thomas.jpg";
import MargaretAvatar from "../Bots/Margaret.jpg";

import FileTreeView from "./FileTreeView"; 

function applyPlaceholders(text) {
  if (!global.placeholders) return text;

  let result = text;

  for (const [placeholder, value] of Object.entries(global.placeholders)) {
    // Escape regex special characters in the placeholder
    const escaped = placeholder.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");

    const regex = new RegExp(escaped, "gi"); // case-insensitive
    result = result.replace(regex, value);
  }

  return result;
}

function getBotWelcomeMessage(bot) {
  switch (bot) {
    case "Sophie":
      return "Hi, I'm Sophie, How can I help you today?";
    case "Eleanor":
      return "Hi, I'm Eleanor, How can I help you today?";
    case "Thomas":
      return "Hello, I'm Thomas, I can help you with customising documents.";
    case "Margaret":
      return "Hello, I'm Margaret, Need help with compliance?";
    default:
      return "Hello! How can I help today?";
  }
}

function Dashboard() {
  const AGENTS = {
    Sophie: {
      url: "https://faas-tor1-70ca848e.doserverless.co/api/v1/web/fn-847a3952-c11c-434f-b908-f0a2b503c72b/default/ask-sophie",             
      title: "Sophie",
      path: "Staff Assistant",
      avatar: SophieAvatar,
      payloadKey: "content",
    },
    Eleanor: {
      url: "https://faas-tor1-70ca848e.doserverless.co/api/v1/web/fn-847a3952-c11c-434f-b908-f0a2b503c72b/default/ask-webbot",
      title: "Eleanor",
      path: "General Help",
      avatar: EleanorAvatar,
      payloadKey: "content",
    },
    Thomas: {
      url: "https://faas-tor1-70ca848e.doserverless.co/api/v1/web/fn-847a3952-c11c-434f-b908-f0a2b503c72b/default/conversation_manager",
      title: "Thomas",
      path: "Help with documents",
      avatar: ThomasAvatar,
      payloadKey: "message",
    },
    Margaret: {
      url: "https://faas-tor1-70ca848e.doserverless.co/api/v1/web/fn-847a3952-c11c-434f-b908-f0a2b503c72b/default/AskConcierge",
      title: "Margaret",
      path: "Help with compliance",
      avatar: MargaretAvatar,
      payloadKey: "content",
    },
  };

  const DOCUMENT_PROCESSOR_URL = "https://faas-tor1-70ca848e.doserverless.co/api/v1/web/fn-847a3952-c11c-434f-b908-f0a2b503c72b/process/document";
  
  // --- SET ROOT PREFIX ---
  const TEMPLATE_PREFIX = "Dom Care Master/"; 

  // --- NEW: Retrieve the organization name placeholder ---
  // We search the global placeholders for the case-insensitive key.
  const orgNameKey = Object.keys(global.placeholders || {}).find(
    key => key.toLowerCase() === '[insert organisation name]'
  );
  const organizationName = orgNameKey 
    ? global.placeholders[orgNameKey] 
    : "Company";
  // ------------------------------------------------------

  const [activeAgent, setActiveAgent] = useState("Sophie");
  const [messages, setMessages] = useState([
    {
    role: "assistant",
    content: getBotWelcomeMessage("Sophie"),
    fromAgent: "Sophie",
    },
  ]);
  // --- New State for View Management ---
  const [activeView, setActiveView] = useState("chat"); // 'chat' or 'files'
  // -------------------------------------

  const [input, setInput] = useState("");
  const [context, setContext] = useState({}); 
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const chatRef = useRef(null);
  const [loading, setLoading] = useState(false);  
  
  // --- PRODUCTION LOGIC: handleCustomizeAndDownload now runs the fetch call ---
  const handleCustomizeAndDownload = async (filePath) => {
    
    // 1. Prepare Payload Data
    const allUpdates = (global.placeholders || {}); 
    
    // 2. Fix Filename Duplication: Strip "Templates/" if present
    let finalFilePath = filePath;
    if (filePath.toLowerCase().startsWith('templates/')) {
        finalFilePath = filePath.substring('templates/'.length); 
    }
    
    // 3. Construct the full S3 key
    const fullTemplateKey = TEMPLATE_PREFIX + finalFilePath; 
    
    const payload = {
        template_key: fullTemplateKey, 
        updates: allUpdates,
    };

    setLoading(true); 

    try {
        const response = await fetch(DOCUMENT_PROCESSOR_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload), // Send payload as JSON string
        });

        // Throw for bad HTTP status (4xx or 5xx)
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        // Check the success flag
        if (data.success && data.download_url) {
            
            // Success: Inform user and initiate download
            alert("Your download will begin shortly.");
            window.open(data.download_url, '_blank');
            
        } else {
            // API-specific error handling
            const errorMsg = data.error || "Unknown error during document processing.";
            
            // Log debug info if available
            if (data.debug) {
                console.error("Document Processor Debug Log:", data.debug);
            }
            alert(`Document processing failed: ${errorMsg}. Please try again later.`);
        }

    } catch (error) {
        console.error("Network or processing error:", error);
        alert(`A network or server error occurred: ${error.message}.`);
    } finally {
        setLoading(false);
    }
  };
  // --------------------------------------------------------------------------------


  const sendMessage = async () => {
    if (!input.trim()) return;
    const userMsg = { role: "user", content: input };
    setMessages((m) => [...m, userMsg]);
    setInput("");

    const agent = AGENTS[activeAgent];
    const bodyPayload = { context }; 
    bodyPayload[agent.payloadKey] = input;
    setLoading(true);   
    try {
      const res = await fetch(agent.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bodyPayload),
      });

      const data = await res.json();
      const reply =
        data.reply || data.answer || "No response from this agent.";

      const botMsg = {
        role: "assistant",
        content: applyPlaceholders(reply),
        fromAgent: activeAgent,
      };

      setMessages((m) => [...m, botMsg]);
      setContext(data.context || {}); 
      setLoading(false);  
    } catch (err) {
      const errMsg = {
        role: "assistant",
        content: "Error: " + err.message,
        fromAgent: activeAgent,
      };
      setMessages((m) => [...m, errMsg]);
      setLoading(false);  
    }
  };

  const handleKeyDown = (e) => e.key === "Enter" && sendMessage();

  useEffect(() => {
    if (chatRef.current)
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  // --- New Function for Agent Selection ---
  const selectAgent = (key) => {
    setActiveAgent(key);
    setMessages([
      {
        role: "assistant",
        content: getBotWelcomeMessage(key),
        fromAgent: key,
      },
    ]);
    setActiveView("chat"); // Switch back to chat view
  };
  // ----------------------------------------


  // --- REMOVED Placeholder for brevity ---


  return (
    <div className="h-screen bg-gray-50 flex flex-col lg:flex-row font-sans">
      {/* Mobile Header (omitted for brevity, assume it's unchanged) */}
      <header className="lg:hidden bg-white border-b p-4 flex items-center justify-between">
        <img
          src="https://cqcexperts.com/wp-content/uploads/2021/05/CQCexperts-header-Logo-optimized.jpg"
          alt="CQCexperts"
          className="h-7"
        />
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-80 bg-white shadow-xl transform transition-transform duration-300 lg:translate-x-0 lg:static lg:z-auto flex flex-col ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-4 lg:hidden flex justify-end">
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 border-b hidden lg:block">
          <img
            src="https://cqcexperts.com/wp-content/uploads/2021/05/CQCexperts-header-Logo-optimized.jpg"
            alt="CQCexperts"
            className="h-8"
          />
        </div>

        {/* Bot Selection */}
        <div className="p-4 border-b flex-1 overflow-y-auto">
          <h3 className="text-sm font-semibold text-teal-900 mb-3">
            Specialists
          </h3>
          <div className="space-y-2">
            {Object.entries(AGENTS).map(([key, { title, path, avatar }]) => (
              <button
                key={key}
                onClick={() => selectAgent(key)} // Use new selectAgent
                className={`w-full text-left p-3 rounded-lg transition flex items-center gap-3 ${
                  activeAgent === key && activeView === "chat" // Update condition
                    ? "bg-teal-600 text-white"
                    : "bg-gray-50 hover:bg-gray-100 text-gray-800"
                }`}
              >
                <img
                  src={avatar}
                  alt={key}
                  className="w-10 h-10 rounded-full object-cover border border-gray-300"
                />
                <div>
                  <p className="font-medium">{title}</p>
                  <p
                    className={`text-xs ${
                      activeAgent === key && activeView === "chat"
                        ? "opacity-90"
                        : "text-gray-500"
                    }`}
                  >
                    {path}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* --- New Button for Documents View --- */}
        <div className="p-4 border-t">
          <button
            onClick={() => setActiveView("files")}
            className={`w-full text-left p-3 rounded-lg transition flex items-center gap-3 font-semibold justify-center ${
              activeView === "files"
                ? "bg-teal-600 text-white"
                : "bg-blue-500 hover:bg-blue-600 text-white"
            }`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            My Company Documents
          </button>
        </div>
        {/* ------------------------------------ */}
      </aside>

      {/* Main Content (Conditional Rendering) */}
      {activeView === "chat" ? (
        <main className="flex-1 flex flex-col">
          {/* Header */}
          <header className="bg-white border-b p-4 flex items-center gap-3">
            <img
              src={AGENTS[activeAgent].avatar}
              alt={activeAgent}
              className="w-10 h-10 rounded-full object-cover border border-gray-300"
            />
            <div>
              <h2 className="font-semibold text-teal-900">
                {AGENTS[activeAgent].title}
              </h2>
              <p className="text-xs text-gray-500">
                Active • {AGENTS[activeAgent].path}
              </p>
            </div>
          </header>

          {/* Messages */}
          <div
            ref={chatRef}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
          >
            <AnimatePresence>
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className={`flex ${
                    msg.role === "user" ? "justify-end" : "justify-start"
                  } gap-3`}
                >
                  {msg.role === "assistant" && (
                    <img
                      src={
                        AGENTS[msg.fromAgent]
                          ? AGENTS[msg.fromAgent].avatar
                          : AGENTS[activeAgent].avatar
                      }
                      alt={msg.fromAgent}
                      className="w-8 h-8 rounded-full object-cover border border-gray-300 flex-shrink-0"
                    />
                  )}
                  <div
                    className={`max-w-md p-4 rounded-2xl shadow-sm ${
                      msg.role === "user"
                        ? "bg-blue-600 text-white"
                        : "bg-green-50 text-gray-800"
                    }`}
                  >
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>
                </motion.div>
              ))}
              {/* Typing Indicator */}
              {loading && (
                <div className="flex justify-start ml-12 mb-4">   
                  <div className="flex space-x-2">
                    <span className="w-2 h-2 bg-black rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-black rounded-full animate-bounce delay-150"></span>
                    <span className="w-2 h-2 bg-black rounded-full animate-bounce delay-300"></span>
                  </div>
                </div>
              )}

            </AnimatePresence>
          </div>

          {/* Input */}
          <footer className="bg-white border-t p-4">
            <div className="flex gap-2 items-center max-w-4xl mx-auto">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                className="flex-1 px-4 py-3 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button
                onClick={sendMessage}
                className="px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition font-medium"
              >
                Send
              </button>
            </div>
          </footer>
        </main>
      ) : (
        <div className="flex-1 p-4 lg:p-8 bg-gray-100">
            <FileTreeView customizationFunction={handleCustomizeAndDownload} orgName={organizationName} /> 
        </div>
      )}
    </div>
  );
}

export default Dashboard;
